import {
  InjectionToken,
  Injectable,
  OnDestroy,
  Injector,
  Optional,
  Inject,
  SkipSelf,
  TemplateRef,
} from '@angular/core';
import {
  Overlay,
  OverlayConfig,
  OverlayContainer,
  OverlayRef,
  ScrollStrategy,
  ComponentType,
} from '@angular/cdk/overlay';
import {
  defer,
  Observable,
  of as observableOf,
  Subject,
  BehaviorSubject,
} from 'rxjs';
import { GritOverlayConfig, OverlayType } from './grit-overlay-config';
import { GritOverlayRef } from './grit-overlay-ref';
import { startWith } from 'rxjs/operators';
import { GritOverlayContainer } from './grit-overlay-container';
import {
  PortalInjector,
  ComponentPortal,
  TemplatePortal,
} from '@angular/cdk/portal';
import { Location } from '@angular/common';
import { Directionality } from '@angular/cdk/bidi';
import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';

export const GRIT_OVERLAY_DATA = new InjectionToken<any>('GritOverlayData');
export const GRIT_OVERLAY_DEFAULT_OPTIONS = new InjectionToken<
  GritOverlayConfig
>('grit-overlay-default-options');

/** Injection token that determines the scroll handling while the dialog is open. */
export const GRIT_OVERLAY_SCROLL_STRATEGY = new InjectionToken<
  () => ScrollStrategy
>('mat-dialog-scroll-strategy');

export function GRIT_OVERLAY_SCROLL_STRATEGY_FACTORY(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

export function GRIT_OVERLAY_SCROLL_STRATEGY_PROVIDER_FACTORY(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

export const GRIT_OVERLAY_SCROLL_STRATEGY_PROVIDER = {
  provide: GRIT_OVERLAY_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: GRIT_OVERLAY_SCROLL_STRATEGY_PROVIDER_FACTORY,
};

@Injectable()
export class GritOverlay implements OnDestroy {
  public _overlayType = new BehaviorSubject<OverlayType>('modal');
  private _openOverlaysAtThisLevel: GritOverlayRef<any>[] = [];
  private readonly _afterAllClosedAtThisLevel = new Subject<void>();
  private readonly _afterOpenedAtThisLevel = new Subject<GritOverlayRef<any>>();
  private _ariaHiddenElements = new Map<Element, string | null>();
  private _scrollStrategy: () => ScrollStrategy;

  /** Keeps track of the currently-open dialogs. */
  get openOverlays(): GritOverlayRef<any>[] {
    return this._parentOverlay
      ? this._parentOverlay.openOverlays
      : this._openOverlaysAtThisLevel;
  }

  get afterOpened(): Subject<GritOverlayRef<any>> {
    return this._parentOverlay
      ? this._parentOverlay.afterOpened
      : this._afterOpenedAtThisLevel;
  }

  get afterOpen(): Subject<GritOverlayRef<any>> {
    return this.afterOpened;
  }

  get _afterAllClosed(): Subject<void> {
    const parent = this._parentOverlay;
    return parent ? parent._afterAllClosed : this._afterAllClosedAtThisLevel;
  }

  readonly afterAllClosed: Observable<void> = defer(() =>
    this.openOverlays.length
      ? this._afterAllClosed
      : this._afterAllClosed.pipe(startWith(undefined))
  ) as Observable<any>;

  _isTablet: boolean = false;

  _overlayBoundingBox;

  constructor(
    private _overlay: Overlay,
    private _injector: Injector,
    @Optional() private _location: Location,
    @Optional()
    @Inject(GRIT_OVERLAY_DEFAULT_OPTIONS)
    private _defaultOptions: GritOverlayConfig,
    @Inject(GRIT_OVERLAY_SCROLL_STRATEGY) scrollStrategy: any,
    @Optional() @SkipSelf() private _parentOverlay: GritOverlay,
    private _overlayContainer: OverlayContainer,
    breakpointObserver: BreakpointObserver,
    mediaMatcher: MediaMatcher
  ) {
    this._scrollStrategy = scrollStrategy;

    breakpointObserver.observe(['(min-width: 600px)']).subscribe(result => {
      if (result.matches) {
        this._isTablet = false;
      } else {
        this._isTablet = true;
      }
    });
  }

  open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: GritOverlayConfig<D>
  ): GritOverlayRef<T, R> {
    config = _applyConfigDefaults(
      config,
      this._defaultOptions || new GritOverlayConfig()
    );

    if (!this._isTablet && config.overlayType === 'bottomSheet') {
      this.updateOverlayType('rightSheet');
      config.overlayType = 'rightSheet';
    } else {
      this.updateOverlayType(config.overlayType);
    }

    if (config.id && this.getDialogById(config.id)) {
      throw Error(
        `Dialog with id "${config.id}" exists already. The dialog id must be unique.`
      );
    }

    const overlayRef = this._createOverlay(config);
    const dialogContainer = this._attachDialogContainer(overlayRef, config);
    const dialogRef = this._attachDialogContent<T, R>(
      componentOrTemplateRef,
      dialogContainer,
      overlayRef,
      config
    );

    // If this is the first dialog that we're opening, hide all the non-overlay content.
    if (!this.openOverlays.length) {
      this._hideNonDialogContentFromAssistiveTechnology();
    }

    this.openOverlays.push(dialogRef);
    dialogRef.afterClosed().subscribe(() => this._removeOpenDialog(dialogRef));
    this.afterOpened.next(dialogRef);
    return dialogRef;
  }

  getCurrentOverlayType(): Observable<OverlayType> {
    return this._overlayType.asObservable();
  }

  updateOverlayType(type) {
    return this._overlayType.next(type);
  }

  closeAll(): void {
    this._closeDialogs(this.openOverlays);
  }

  getDialogById(id: string): GritOverlayRef<any> | undefined {
    return this.openOverlays.find(dialog => dialog.id === id);
  }

  ngOnDestroy() {
    this._closeDialogs(this._openOverlaysAtThisLevel);
    this._afterAllClosedAtThisLevel.complete();
    this._afterOpenedAtThisLevel.complete();
  }

  private _attachDialogContent<T, R>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    dialogContainer: GritOverlayContainer,
    overlayRef: OverlayRef,
    config: GritOverlayConfig
  ): GritOverlayRef<T, R> {
    // Create a reference to the dialog we're creating in order to give the user a handle
    // to modify and close it.
    const dialogRef = new GritOverlayRef<T, R>(
      overlayRef,
      dialogContainer,
      this._location,
      config.id
    );

    // When the dialog backdrop is clicked, we want to close it.
    if (config.hasBackdrop) {
      overlayRef.backdropClick().subscribe(() => {
        if (!dialogRef.disableClose) {
          dialogRef.close();
        }
      });
    }

    if (componentOrTemplateRef instanceof TemplateRef) {
      dialogContainer.attachTemplatePortal(
        new TemplatePortal<T>(componentOrTemplateRef, null!, <any>{
          $implicit: config.data,
          dialogRef,
        })
      );
    } else {
      const injector = this._createInjector<T>(
        config,
        dialogRef,
        dialogContainer
      );
      const contentRef = dialogContainer.attachComponentPortal<T>(
        new ComponentPortal(componentOrTemplateRef, undefined, injector)
      );
      dialogRef.componentInstance = contentRef.instance;
    }

    dialogRef
      .updateSize(config.maxWidth, config.minHeight)
      .updatePosition(config.position);

    return dialogRef;
  }

  private _createInjector<T>(
    config: GritOverlayConfig,
    dialogRef: GritOverlayRef<T>,
    dialogContainer: GritOverlayContainer
  ): PortalInjector {
    const userInjector =
      config && config.viewContainerRef && config.viewContainerRef.injector;

    const injectionTokens = new WeakMap<any, any>([
      [GritOverlayContainer, dialogContainer],
      [GRIT_OVERLAY_DATA, config.data],
      [GritOverlayRef, dialogRef],
    ]);

    if (
      config.direction &&
      (!userInjector ||
        !userInjector.get<Directionality | null>(Directionality, null))
    ) {
      injectionTokens.set(Directionality, {
        value: config.direction,
        change: observableOf(),
      });
    }

    return new PortalInjector(userInjector || this._injector, injectionTokens);
  }

  private _attachDialogContainer(
    overlay: OverlayRef,
    config: GritOverlayConfig
  ): GritOverlayContainer {
    const userInjector =
      config && config.viewContainerRef && config.viewContainerRef.injector;
    const injector = new PortalInjector(
      userInjector || this._injector,
      new WeakMap([[GritOverlayConfig, config]])
    );
    const containerPortal = new ComponentPortal(
      GritOverlayContainer,
      config.viewContainerRef,
      injector,
      config.componentFactoryResolver
    );
    const containerRef = overlay.attach<GritOverlayContainer>(containerPortal);
    return containerRef.instance;
  }

  private _createOverlay(config: GritOverlayConfig): OverlayRef {
    switch (config.overlayType) {
      case 'bottomSheet':
        this._overlayBoundingBox = this._createBottomSheetPosition();
        config.position = this._overlayBoundingBox.position;
        break;

      case 'rightSheet':
        this._overlayBoundingBox = this._createRightSheetPosition(
          config.maxWidth,
          config.minHeight
        );
        config.position = this._overlayBoundingBox.position;
        break;

      case 'modal':
        this._overlayBoundingBox = this._createModalPosition(
          config.maxWidth,
          config.minHeight
        );
        config.position = this._overlayBoundingBox.position;
        break;
    }

    const overlayConfig = this._getOverlayConfig(config);
    return this._overlay.create(overlayConfig);
  }

  private _createModalPosition(width?, height?) {
    let sheetDefaultSize;
    const updatedPosition = {};
    if (width || height) {
      sheetDefaultSize = { width: width, height: height };
    } else {
      sheetDefaultSize = { width: '400px', height: 'auto' };
    }

    const sheetBoundingBox = {
      position: updatedPosition,
      size: sheetDefaultSize,
    };
    return sheetBoundingBox;
  }

  private _createBottomSheetPosition() {
    const updatedPosition = { top: 'auto', bottom: '0' };
    const sheetDefaultSize = { width: '100vw', height: 'auto' };
    const sheetBoundingBox = {
      position: updatedPosition,
      size: sheetDefaultSize,
    };
    return sheetBoundingBox;
  }

  private _createRightSheetPosition(width?, height?) {
    let sheetDefaultSize;
    const updatedPosition = {
      top: '0px',
      bottom: '0px',
      left: 'auto',
      right: '0px',
    };

    if (width && !this._isTablet) {
      sheetDefaultSize = {
        width: `${width}px`,
        height: `100vh`,
      };
    } else if (!width && !this._isTablet) {
      sheetDefaultSize = {
        width: `375px`,
        height: `100vh`,
      };
    } else if (this._isTablet) {
      sheetDefaultSize = {
        width: `600px`,
        height: `100vh`,
      };
    }

    const sheetBoundingBox = {
      position: updatedPosition,
      size: sheetDefaultSize,
    };

    return sheetBoundingBox;
  }

  private _getOverlayConfig(dialogConfig: GritOverlayConfig): OverlayConfig {
    const state = new OverlayConfig({
      positionStrategy: this._overlay.position().global(),
      scrollStrategy: dialogConfig.scrollStrategy || this._scrollStrategy(),
      panelClass: dialogConfig.backdropClass,
      hasBackdrop: dialogConfig.hasBackdrop,
      backdropClass: dialogConfig.backdropClass
        ? dialogConfig.backdropClass
        : this._getBackdrop(dialogConfig.overlayType),
      direction: dialogConfig.direction,
      minHeight: this._overlayBoundingBox.size.height,
      maxWidth: this._overlayBoundingBox.size.width,
      disposeOnNavigation: dialogConfig.closeOnNavigation,
    });

    if (dialogConfig.backdropClass) {
      state.backdropClass = dialogConfig.backdropClass;
    }

    return state;
  }

  private _getBackdrop(type: OverlayType) {
    if (type === 'modal') {
      return 'grit-modal-background';
    } else {
      return 'grit-sheet-background';
    }
  }

  private _removeOpenDialog(dialogRef: GritOverlayRef<any>) {
    const index = this.openOverlays.indexOf(dialogRef);
    if (index > -1) {
      this.openOverlays.splice(index, 1);

      // If all the dialogs were closed, remove/restore the `aria-hidden`
      // to a the siblings and emit to the `afterAllClosed` stream.
      if (!this.openOverlays.length) {
        this._ariaHiddenElements.forEach((previousValue, element) => {
          if (previousValue) {
            element.setAttribute('aria-hidden', previousValue);
          } else {
            element.removeAttribute('aria-hidden');
          }
        });

        this._ariaHiddenElements.clear();
        this._afterAllClosed.next();
      }
    }
  }

  private _closeDialogs(dialogs: GritOverlayRef<any>[]) {
    let i = dialogs.length;

    while (i--) {
      dialogs[i].close();
    }
  }

  private _hideNonDialogContentFromAssistiveTechnology() {
    const overlayContainer = this._overlayContainer.getContainerElement();

    // Ensure that the overlay container is attached to the DOM.
    if (overlayContainer.parentElement) {
      const siblings = overlayContainer.parentElement.children;

      for (let i = siblings.length - 1; i > -1; i--) {
        let sibling = siblings[i];

        if (
          sibling !== overlayContainer &&
          sibling.nodeName !== 'SCRIPT' &&
          sibling.nodeName !== 'STYLE' &&
          !sibling.hasAttribute('aria-live')
        ) {
          this._ariaHiddenElements.set(
            sibling,
            sibling.getAttribute('aria-hidden')
          );
          sibling.setAttribute('aria-hidden', 'true');
        }
      }
    }
  }
}

function _applyConfigDefaults(
  config?: GritOverlayConfig,
  defaultOptions?: GritOverlayConfig
): GritOverlayConfig {
  return { ...defaultOptions, ...config };
}
